// apps/web/src/features/crm/leads/card/useLeadStageActions.ts
//
// Bridges the v4 card's funnel controls to the LEGACY lead stage mutation
// (#2247 FR-2/FR-3). Every move — into-work, postpone, refuse, non-target,
// back-to-work — is a `PATCH /leads/:id/stage` target through the reused
// `useTransitionLeadStage` hook, never an `/engagements/*` mutation (those are
// dormant for projected rows). The legacy hook only invalidates `leadsKeys`, so
// the success handler additionally flushes `engagementsKeys` so the card's
// projection header re-reads the fresh funnel state after the write.
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { LeadStageCode } from '@magimp/shared';
import { engagementsKeys, useTransitionLeadStage } from '@magimp/shared-hooks';
import { useToastStore } from '../../../../stores/toast.js';
import { errorMessage } from '../../../../api/errors.js';

export interface LeadStageActions {
  run: (toStage: LeadStageCode) => void;
  isPending: boolean;
}

export function useLeadStageActions(leadId: string): LeadStageActions {
  const { t } = useTranslation(['leads']);
  const qc = useQueryClient();
  const show = useToastStore((s) => s.show);
  const transition = useTransitionLeadStage(leadId);

  return {
    run: (toStage) => {
      if (transition.isPending) return;
      transition.mutate(
        { toStage },
        {
          onSuccess: () => {
            show({ tone: 'success', text: t('leads:toasts.stageChanged') });
            // Reads-from-engagement, writes-to-leads: flush the projection the
            // lead-scoped hook cannot reach.
            void qc.invalidateQueries({ queryKey: engagementsKeys.all });
          },
          onError: (err) => show({ tone: 'error', text: errorMessage(err) }),
        },
      );
    },
    isPending: transition.isPending,
  };
}
